import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { DashboardSummary, PurchaseOrderDetail } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import SeverityBadge from '@/components/ui/SeverityBadge';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
}

function todayLong(): string {
  return new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function DashboardOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [drafts, setDrafts] = useState<PurchaseOrderDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadData = async () => {
    try {
      const [summaryResult, poResult] = await Promise.all([
        api.getDashboardSummary(),
        api.getPurchaseOrders().catch(() => [] as PurchaseOrderDetail[]),
      ]);
      setSummary(summaryResult);
      setDrafts(poResult.filter((po) => po.status === 'DRAFT'));
    } catch (err) {
      console.error('Fehler beim Laden des Dashboards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.triggerSync();
      await loadData();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  const okShare = summary.totalProducts > 0
    ? Math.round((summary.green / summary.totalProducts) * 100)
    : 0;
  const draftItemCount = drafts.reduce((sum, po) => sum + po.itemCount, 0);
  const firstName = user?.name?.split(' ')[0] ?? '';

  const metricCards: Array<{
    label: string;
    value: string;
    sub: string;
    color: string;
    dotBg: string;
    dot: string;
  }> = [
    {
      label: 'Umsatzrisiko',
      value: formatCurrency(summary.totalRiskRevenue),
      sub: `${summary.red} Artikel kritisch`,
      color: summary.totalRiskRevenue > 0 ? 'text-risk-red' : 'text-ink',
      dotBg: 'bg-risk-red-soft',
      dot: 'bg-risk-red',
    },
    {
      label: 'DB-Risiko',
      value: formatCurrency(summary.totalRiskContributionMargin),
      sub: 'gefährdeter Deckungsbeitrag',
      color: summary.totalRiskContributionMargin > 0 ? 'text-risk-yellow' : 'text-ink',
      dotBg: 'bg-risk-yellow-soft',
      dot: 'bg-risk-yellow',
    },
    {
      label: 'Bestand i.O.',
      value: `${okShare}%`,
      sub: `${summary.green} von ${summary.totalProducts} Artikeln`,
      color: 'text-brand-deep',
      dotBg: 'bg-brand-soft',
      dot: 'bg-brand',
    },
    {
      label: 'Offene Bestelllisten',
      value: String(drafts.length),
      sub: `${draftItemCount} Artikel insgesamt`,
      color: 'text-ink',
      dotBg: 'bg-surface-2',
      dot: 'bg-ink',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">
            {greeting()}{firstName ? `, ${firstName}` : ''} 👋
          </h1>
          <p className="text-[13.5px] text-ink-muted mt-1">
            {todayLong()} · {summary.red} Artikel kritisch, {summary.yellow} mit Warnung — hier
            ist dein Tag.
          </p>
        </div>
        <button onClick={handleSync} disabled={syncing} className="btn-primary">
          {syncing ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Sync & Berechnung
        </button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-card border border-line bg-surface px-5 py-[18px]">
            <div className="flex justify-between items-start">
              <span className="text-[12.5px] font-semibold text-ink-muted">{m.label}</span>
              <span
                className={clsx(
                  'w-[26px] h-[26px] rounded-lg flex items-center justify-center',
                  m.dotBg
                )}
              >
                <span className={clsx('w-[9px] h-[9px] rounded-full', m.dot)} />
              </span>
            </div>
            <div
              className={clsx(
                'text-[32px] font-extrabold tracking-tight leading-none mt-3.5 mb-1.5',
                m.color
              )}
            >
              {m.value}
            </div>
            <div className="font-mono text-[11px] text-ink-soft">{m.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start">
        {/* Risk table */}
        <div className="rounded-card border border-line bg-surface overflow-hidden">
          <div className="px-5 py-4 border-b border-line flex items-center justify-between">
            <div className="flex items-baseline gap-2.5">
              <h2 className="text-[16.5px] font-extrabold text-ink">Lagerrisiken</h2>
              <span className="font-mono text-[11px] text-ink-soft">nächste Stockouts</span>
            </div>
            <Link
              to="/lagerrisiken"
              className="text-sm font-bold text-brand-deep bg-brand-soft px-3.5 py-1.5 rounded-full hover:bg-brand-100 transition-colors flex items-center gap-1"
            >
              Alle anzeigen <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {summary.nextOosTop5.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-ink-soft">
              Keine gefährdeten Artikel — alles im grünen Bereich.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Artikel', 'Reichweite', 'OOS-Datum', 'Status', 'Risiko'].map((h, i) => (
                      <th
                        key={h}
                        className={clsx(
                          'px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3',
                          i >= 1 && i !== 3 ? 'text-right' : 'text-left'
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.nextOosTop5.map((item, i) => (
                    <tr
                      key={item.sku}
                      onClick={() => navigate(`/products/${encodeURIComponent(item.sku)}`)}
                      className={clsx(
                        'cursor-pointer bg-surface transition-colors hover:bg-surface-2',
                        i < summary.nextOosTop5.length - 1 && 'border-b border-surface-3'
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="text-[13.5px] font-bold text-ink">{item.name}</div>
                        <div className="font-mono text-[10.5px] text-ink-soft mt-px">
                          {item.sku}
                        </div>
                      </td>
                      <td
                        className={clsx(
                          'px-4 py-3 text-right font-mono text-[12.5px] font-medium',
                          item.coverageDays <= 7
                            ? 'text-risk-red'
                            : item.coverageDays <= 14
                              ? 'text-risk-yellow'
                              : 'text-ink-muted'
                        )}
                      >
                        {Math.round(item.coverageDays)} T.
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink-muted">
                        {formatDate(item.oosDate)}
                      </td>
                      <td className="px-4 py-3">
                        <SeverityBadge severity={item.effectiveSeverity} />
                      </td>
                      <td
                        className={clsx(
                          'px-4 py-3 text-right font-mono text-[13px]',
                          (item.riskValue ?? 0) > 0 ? 'font-bold text-risk-red' : 'text-ink-soft'
                        )}
                      >
                        {(item.riskValue ?? 0) > 0 ? formatCurrency(item.riskValue ?? 0) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Side panel */}
        <div className="flex flex-col gap-4">
          <div className="rounded-card border border-line bg-surface p-5">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-[16.5px] font-extrabold text-ink">Bestelllisten</h2>
              <span className="font-mono text-[11px] text-ink-soft">offen</span>
            </div>
            {drafts.length === 0 ? (
              <div className="text-sm text-ink-soft">
                Keine offenen Bestelllisten.{' '}
                <Link to="/lagerrisiken" className="font-bold text-brand-deep hover:underline">
                  Aus Lagerrisiken erstellen →
                </Link>
              </div>
            ) : (
              drafts.slice(0, 4).map((po, i) => (
                <div
                  key={po.id}
                  className={clsx(
                    'bg-surface-2 rounded-panel px-4 py-3.5',
                    i < Math.min(drafts.length, 4) - 1 && 'mb-3'
                  )}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-bold text-ink">
                      Liste vom {formatDate(po.createdAt)}
                    </span>
                    <span className="font-mono text-[11px] text-ink-muted">
                      {po.itemCount} Artikel
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-[11px] text-ink-muted">
                      {po.generatedByName}
                    </span>
                    <Link
                      to={`/bestelllisten/${po.id}`}
                      className="text-xs font-bold text-white bg-brand px-3.5 py-1.5 rounded-full hover:bg-brand-deep transition-colors"
                    >
                      Prüfen →
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="rounded-card border border-brand-soft-border bg-brand-soft p-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-brand-deep mb-2">
              Im Blick
            </div>
            <div className="text-[30px] font-extrabold tracking-tight leading-none text-brand-deep">
              {formatCurrency(summary.totalRiskRevenue)}
            </div>
            <p className="text-[13px] text-ink-muted leading-relaxed mt-2.5">
              Umsatzrisiko über alle gefährdeten Artikel — rechtzeitig nachbestellt, bevor der
              erste Kunde „nicht verfügbar" sieht.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
